import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PasswordField } from "@/components/auth/PasswordField";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { runPendingSaveAfterAuth, getPendingSave } from "@/lib/pendingSave";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inlineError, setInlineError] = useState("");
  const [loading, setLoading] = useState(false);

  // Public-facing tab title for the login page.
  useEffect(() => {
    const prev = document.title;
    document.title = "Anokina Studio — Лаборатория превращений";
    return () => { document.title = prev; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setInlineError("");

    // Empty-field guard (does NOT reveal whether the account exists).
    if (!email.trim() || !password) {
      setInlineError("Введите email и пароль");
      return;
    }

    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Добро пожаловать");
      const pending = getPendingSave();
      await runPendingSaveAfterAuth();
      // Return-path handoff (e.g. from /master-order-testing when opened anonymously).
      let postLogin = null;
      try {
        postLogin = sessionStorage.getItem("post_login_redirect");
        if (postLogin) sessionStorage.removeItem("post_login_redirect");
      } catch {}
      if (postLogin && postLogin.startsWith("/") && !postLogin.startsWith("//")) {
        nav(postLogin, { replace: true });
      } else if (pending) {
        // If the user came here from a "Save" click, drop them back where they were.
        nav(`/library`, { replace: true });
      } else {
        nav("/hall", { replace: true });
      }
    } else {
      // Classify: 401 (bad credentials) vs anything else (network/server).
      //   AuthContext returns formatApiError(err) as res.error.
      //   Backend replies 401 with Russian detail "Неверный email или пароль",
      //   which formatApiError forwards verbatim. Network / CORS / 5xx errors
      //   surface as generic strings ("Network Error", "timeout", the fallback
      //   "Что-то пошло не так", or an empty message).
      const err = String(res.error || "").toLowerCase();
      const looksLikeBadCredentials =
        err.includes("неверн") ||
        err.includes("invalid") ||
        err.includes("wrong") ||
        err.includes("password");
      if (looksLikeBadCredentials) {
        setInlineError("Неверный email или пароль");
      } else {
        setInlineError("Не удалось войти. Проверьте соединение и попробуйте ещё раз");
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div
        className="flex-1 flex items-end"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(74,59,50,0.10) 0%, rgba(74,59,50,0.55) 100%), url('https://customer-assets-rejwkqb3.emergentagent.net/job_4fdfd6f9-6803-4276-8258-f36ae827b1f3/artifacts/pntpjlxa_79B8E46A-C20C-47B8-AD98-722E2F90A324.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="w-full bg-white rounded-t-[32px] px-7 pt-9 pb-10 shadow-[0_-12px_40px_rgba(0,0,0,0.15)]">
          <div className="mb-7">
            <div className="chip bg-sand/60 text-espresso inline-block mb-3">Anokina Studio</div>
            <h1 className="font-serif text-4xl leading-tight text-espresso" data-testid="login-brand-title">
              Лаборатория<br />превращений
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Войдите, чтобы продолжить работу над проектами.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Email</label>
              <Input
                data-testid="login-email-input"
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (inlineError) setInlineError(""); }}
                required
                className="mt-1 h-12 rounded-2xl"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Пароль</label>
              <PasswordField
                data-testid="login-password-input"
                toggleTestId="login-password-toggle"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (inlineError) setInlineError(""); }}
                required
                className="h-12 rounded-2xl"
              />
            </div>

            {inlineError && (
              <div
                data-testid="login-inline-error"
                role="alert"
                aria-live="polite"
                className="text-sm rounded-xl px-4 py-3"
                style={{ background: "#F7E6DE", color: "#7A2E17" }}
              >
                {inlineError}
              </div>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="btn-pill w-full h-12 bg-espresso hover:bg-espressoDark text-white text-base"
            >
              {loading ? "Входим…" : "Войти"}
            </Button>
            <div className="text-center">
              <Link
                data-testid="link-to-forgot-password"
                to="/forgot-password"
                className="text-xs uppercase tracking-widest text-espresso/70 hover:text-espresso underline underline-offset-4"
              >
                Забыли пароль?
              </Link>
            </div>
          </form>

          <p className="text-sm text-center mt-6 text-muted-foreground">
            Нет аккаунта?{" "}
            <Link data-testid="link-to-register" to="/register" className="text-espresso font-medium underline underline-offset-4">
              Создать аккаунт
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
